import { morph, MORPH_OPTIONS } from '@theme/morph';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

/**
 * Shipping Protection toggle for the cart drawer / cart page.
 *
 * Checking the box adds the protection product, unchecking removes it. The
 * cart section is morphed in place afterwards so the drawer reflects the new
 * state without a page reload.
 */

const CHECKBOX_SELECTOR = '.shipping-protection-checkbox';
const CART_SECTION_SELECTOR = 'cart-items-component[data-section-id]';

let pending = false;
const protectionVariantIds = new Set();

/**
 * @returns {string[]} Section ids of every cart section on the page.
 */
function getCartSectionIds() {
  /** @type {string[]} */
  const ids = [];

  for (const element of document.querySelectorAll(CART_SECTION_SELECTOR)) {
    const { sectionId } = /** @type {HTMLElement} */ (element).dataset;

    if (sectionId) ids.push(sectionId);
  }

  return ids;
}

/**
 * @returns {Promise<any>} The current cart.
 */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });

  return response.json();
}

/**
 * @param {string} url
 * @param {Record<string, unknown>} body
 * @returns {Promise<any>}
 */
async function postToCart(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      ...body,
      sections: getCartSectionIds().join(','),
      sections_url: window.location.pathname,
    }),
  });

  const data = await response.json();

  if (!response.ok) throw data;

  return data;
}

/**
 * @param {string} variantId
 * @param {any} cart
 * @returns {Promise<any>}
 */
function addProtection(variantId, cart) {
  const existing = cart.items.find(
    (/** @type {any} */ item) => String(item.variant_id) === String(variantId)
  );

  // Already present (e.g. added from elsewhere) - normalise to a single unit.
  if (existing) {
    return postToCart(Theme.routes.cart_change_url, { id: existing.key, quantity: 1 });
  }

  return postToCart(Theme.routes.cart_add_url, {
    items: [{ id: Number(variantId), quantity: 1 }],
  });
}

/**
 * @param {string} variantId
 * @param {any} cart
 * @returns {Promise<any>}
 */
function removeProtection(variantId, cart) {
  const existing = cart.items.find(
    (/** @type {any} */ item) => String(item.variant_id) === String(variantId)
  );

  if (!existing) return Promise.resolve(null);

  return postToCart(Theme.routes.cart_change_url, { id: existing.key, quantity: 0 });
}

/**
 * @param {string} sectionId
 * @returns {Promise<string>} Freshly rendered section markup.
 */
async function fetchSectionHtml(sectionId) {
  const url = new URL(window.location.href);
  url.searchParams.set('section_id', sectionId);

  const response = await fetch(url.toString(), { cache: 'no-store' });

  return response.text();
}

/**
 * Morphs a single cart section, mirroring how the theme refreshes the cart
 * after a quantity change. Falls back to morphing just the cart section
 * element if the full section wrapper isn't in the response.
 *
 * @param {string} sectionId
 * @param {string} html
 */
async function morphCartSection(sectionId, html) {
  const selector = `cart-items-component[data-section-id="${CSS.escape(sectionId)}"]`;
  const current = document.querySelector(selector);

  if (!current) return;

  try {
    await morphSection(sectionId, html, current.hasAttribute('data-drawer') ? 'hydration' : 'full');
    return;
  } catch {
    // Section wrapper missing from the response - handled below.
  }

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const next = parsed.querySelector(selector) ?? parsed.querySelector('cart-items-component');

  if (next) morph(current, next, MORPH_OPTIONS);
}

/**
 * Morphs every cart section on the page with the given (or freshly fetched) markup.
 *
 * @param {Record<string, string> | undefined} sections
 */
async function refreshCartSections(sections) {
  await Promise.all(
    getCartSectionIds().map(async (sectionId) => {
      const html = sections?.[sectionId] ?? (await fetchSectionHtml(sectionId));

      await morphCartSection(sectionId, html);
    })
  );
}

/**
 * Reflects cart contents onto every protection checkbox.
 *
 * @param {any} cart
 */
function syncCheckboxes(cart) {
  const variantIds = (cart?.items ?? []).map((/** @type {any} */ item) => String(item.variant_id));

  for (const checkbox of document.querySelectorAll(CHECKBOX_SELECTOR)) {
    const input = /** @type {HTMLInputElement} */ (checkbox);
    const { variant } = input.dataset;

    if (!variant) continue;

    input.checked = variantIds.includes(String(variant));
    input.disabled = false;
  }
}

/**
 * @param {boolean} isDisabled
 */
function setCheckboxesDisabled(isDisabled) {
  for (const checkbox of document.querySelectorAll(CHECKBOX_SELECTOR)) {
    /** @type {HTMLInputElement} */ (checkbox).disabled = isDisabled;
  }
}

/**
 * Remembers configured protection variants before a cart morph can remove
 * their checkboxes from the DOM.
 */
function rememberProtectionVariantIds() {
  for (const script of document.querySelectorAll('[data-shipping-protection-variants]')) {
    const variantIds = /** @type {HTMLElement} */ (script).dataset.shippingProtectionVariants;

    for (const variantId of variantIds?.split(',') ?? []) {
      if (variantId) protectionVariantIds.add(variantId);
    }
  }

  for (const checkbox of document.querySelectorAll(CHECKBOX_SELECTOR)) {
    const variantId = /** @type {HTMLInputElement} */ (checkbox).dataset.variant;

    if (variantId) protectionVariantIds.add(String(variantId));
  }
}

/**
 * Removes protection when no regular line items remain.
 *
 * @param {any} cart
 * @returns {Promise<boolean>} Whether protection was removed.
 */
async function removeOrphanedProtection(cart) {
  rememberProtectionVariantIds();

  if (!cart?.items?.length || protectionVariantIds.size === 0) return false;

  const containsOnlyProtection = cart.items.every((/** @type {any} */ item) =>
    protectionVariantIds.has(String(item.variant_id))
  );

  if (!containsOnlyProtection) return false;

  /** @type {Record<string, number>} */
  const updates = {};

  for (const item of cart.items) {
    updates[item.key] = 0;
  }

  const result = await postToCart(Theme.routes.cart_update_url, { updates });
  const sections = result?.sections;

  await refreshCartSections(sections);

  const updatedCart = await fetchCart();

  syncCheckboxes(updatedCart);
  announceCartUpdate(updatedCart, sections);

  return true;
}

/**
 * @param {any} cart
 * @param {Record<string, string> | undefined} sections
 */
function announceCartUpdate(cart, sections) {
  // Dispatched from the cart section so its own handler skips the redundant
  // re-render, while cart icon / totals listeners on document still react.
  const source = document.querySelector(CART_SECTION_SELECTOR) ?? document.documentElement;

  source.dispatchEvent(
    new CartUpdateEvent(cart, 'shipping-protection', {
      source: 'shipping-protection',
      itemCount: cart?.item_count ?? 0,
      sections: sections ?? {},
    })
  );
}

/**
 * @param {Event} event
 */
async function onChange(event) {
  const target = event.target;

  if (!(target instanceof HTMLInputElement) || !target.matches(CHECKBOX_SELECTOR)) return;

  const variantId = target.dataset.variant;

  if (!variantId) return;

  if (pending) {
    target.checked = !target.checked;
    return;
  }

  const shouldAdd = target.checked;

  pending = true;
  setCheckboxesDisabled(true);

  try {
    const cart = await fetchCart();
    const result = shouldAdd
      ? await addProtection(variantId, cart)
      : await removeProtection(variantId, cart);

    const sections = result?.sections;

    await refreshCartSections(sections);

    const updatedCart = await fetchCart();

    syncCheckboxes(updatedCart);
    announceCartUpdate(updatedCart, sections);
  } catch (error) {
    console.error('Shipping protection error:', error);
    target.checked = !shouldAdd;
  } finally {
    pending = false;
    setCheckboxesDisabled(false);
  }
}

/**
 * Keeps checkboxes accurate when the cart changes from anywhere else.
 *
 * @param {Event} event
 */
async function onCartUpdate(event) {
  const detail = /** @type {any} */ (event).detail;

  if (detail?.data?.source === 'shipping-protection') return;
  if (pending) return;

  pending = true;
  setCheckboxesDisabled(true);

  try {
    const cart = await fetchCart();
    const removed = await removeOrphanedProtection(cart);

    if (!removed) syncCheckboxes(cart);
  } catch (error) {
    console.error('Shipping protection cleanup error:', error);
  } finally {
    pending = false;
    setCheckboxesDisabled(false);
  }
}

document.addEventListener('change', onChange);
document.addEventListener('cart:update', onCartUpdate);

// Also enforce the rule for carts restored on the initial page load.
onCartUpdate(new CustomEvent('cart:update'));
